import DiagramNode from "./DiagramNode";
import DiagramEdge from "@semantical/core/modules/diagram/DiagramEdge";
import { Individual } from "@semantical/core/modules/knowledge-graph";
import { isIndividual } from "@semantical/core/modules/workspace";
export default class Diagram {
    constructor(name, boundedContextId, id) {
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** The BoundedContext this diagram belongs to. Immutable once set. */
        Object.defineProperty(this, "boundedContextId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** The dataset (KnowledgeGraph) this diagram is linked to. New elements are created in this dataset. */
        Object.defineProperty(this, "datasetId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "nodes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "edges", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "inferredEdges", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        }); // Inferred by inference engine, not persisted
        Object.defineProperty(this, "edgeStyle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'arc'
        }); // Default to arc style for new diagrams
        Object.defineProperty(this, "anchorMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'dynamic'
        }); // Default to dynamic anchor mode for new diagrams
        Object.defineProperty(this, "forceLayoutEnabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        }); // Whether force-directed layout is enabled
        Object.defineProperty(this, "viewportTransform", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { x: 0, y: 0, k: 1 }
        }); // Viewport pan/zoom state
        // Display options
        Object.defineProperty(this, "showStereotype", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "showEssentialProperties", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "showOptionalProperties", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "inferenceEnabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "editingMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'ABox'
        });
        // Modeling Language - the ontology used for diagram visualization (diagram-level, not context-level)
        Object.defineProperty(this, "modelingLanguageIri", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // Labeling Context - the SKOS vocabulary from the Ubiquitous Language used for labeling
        Object.defineProperty(this, "labelingContextIri", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // Editor toolbar options
        Object.defineProperty(this, "showGrid", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "showToolbar", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "snapToGrid", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        // Visualization layers
        Object.defineProperty(this, "showDomainVisualization", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.id = id ?? crypto.randomUUID();
        this.name = name;
        this.boundedContextId = boundedContextId;
    }
    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON() {
        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#',
                nodes: { '@id': 'hasNode', '@type': '@id', '@container': '@set' },
                edges: { '@id': 'hasEdge', '@type': '@id', '@container': '@set' }
            },
            '@type': 'Diagram',
            '@id': `urn:semantical:diagram:${this.id}`,
            id: this.id,
            name: this.name,
            boundedContextId: this.boundedContextId,
            datasetId: this.datasetId,
            nodes: this.nodes.map((node) => node.toJSON()),
            edges: this.edges.map((edge) => edge.toJSON()),
            edgeStyle: this.edgeStyle,
            anchorMode: this.anchorMode,
            forceLayoutEnabled: this.forceLayoutEnabled,
            viewportTransform: this.viewportTransform,
            showStereotype: this.showStereotype,
            showEssentialProperties: this.showEssentialProperties,
            showOptionalProperties: this.showOptionalProperties,
            inferenceEnabled: this.inferenceEnabled,
            editingMode: this.editingMode,
            modelingLanguageIri: this.modelingLanguageIri,
            labelingContextIri: this.labelingContextIri,
            showGrid: this.showGrid,
            showToolbar: this.showToolbar,
            snapToGrid: this.snapToGrid,
            showDomainVisualization: this.showDomainVisualization
        };
    }
    addNode(node) {
        // Check if an element with the same URI already exists
        const exists = this.nodes.some(existing => existing.modelElement.uri === node.modelElement.uri);
        if (exists) {
            console.debug(`Diagram.addNode: Element with URI "${node.modelElement.uri}" already exists on diagram`);
            return false;
        }
        this.nodes.push(node);
        return true;
    }
    addEdge(diagramEdge) {
        this.edges.push(diagramEdge);
    }
    removeNode(diagramNode) {
        this.nodes = this.nodes.filter(n => n !== diagramNode);
    }
    removeEdge(diagramEdge) {
        this.edges = this.edges.filter(e => e !== diagramEdge);
    }
    /**
     * Set the edge style.
     * Fixed anchor positions are preserved - Arc mode simply ignores them
     * while S-Curve mode uses them if set.
     */
    setEdgeStyle(style) {
        this.edgeStyle = style;
        // Note: We no longer reset anchors here. Arc mode ignores fixed positions
        // but preserves them so they're restored when switching back to S-Curve.
    }
    /**
     * Set the anchor positioning mode and reset all fixed anchors.
     * When switching modes, anchors need to be recomputed for the new algorithm.
     */
    setAnchorMode(mode) {
        this.anchorMode = mode;
        // Reset all fixed anchors so they get recomputed for the new mode
        for (const edge of this.edges) {
            edge.resetAnchors();
        }
    }
    /**
     * @deprecated Ontology content is now server-side only.
     * Placeholder resolution is handled by DiagramViewModel.
     * This method is kept for backward compatibility but does nothing.
     */
    resolvePlaceholders(_boundedContext) {
        // Ontology content is server-side only - no client-side resolution
        return 0;
    }
    /**
     * Set the viewport transform (pan/zoom state).
     */
    setViewportTransform(transform) {
        this.viewportTransform = transform;
    }
    /**
     * Set the modeling language (ontology IRI) for this diagram.
     * This is diagram-level configuration that does not affect the BoundedContext's semantic layer.
     */
    setModelingLanguage(iri) {
        this.modelingLanguageIri = iri;
    }
    /**
     * Set the labeling context (SKOS vocabulary IRI) for this diagram.
     * Must be a vocabulary from the BoundedContext's Ubiquitous Language.
     */
    setLabelingContext(iri) {
        this.labelingContextIri = iri;
    }
    getEdgesForNode(diagramNode) {
        return this.edges.filter(e => e.source === diagramNode || e.target === diagramNode);
    }
    /**
     * Get all edges including inferred ones.
     */
    getAllEdges() {
        return [...this.edges, ...this.inferredEdges];
    }
    /**
     * Set the inferred edges (replaces any existing).
     * Called by the inference engine when inference is enabled.
     */
    setInferredEdges(inferred) {
        this.inferredEdges = inferred;
    }
    /**
     * Clear all inferred edges.
     * Called when inference is disabled.
     */
    clearInferredEdges() {
        this.inferredEdges = [];
    }
    /**
     * Get visible (non-hidden) nodes.
     */
    getVisibleNodes() {
        return this.nodes.filter(n => !n.hidden);
    }
    /**
     * Get hidden nodes.
     */
    getHiddenNodes() {
        return this.nodes.filter(n => n.hidden);
    }
    /**
     * Check if any nodes are hidden.
     */
    hasHiddenNodes() {
        return this.nodes.some(n => n.hidden);
    }
    /**
     * Show all hidden nodes.
     */
    showAllNodes() {
        this.nodes.forEach(n => n.hidden = false);
    }
    /**
     * Show all hidden edges.
     */
    showAllEdges() {
        this.edges.forEach(e => e.hidden = false);
    }
    /**
     * Show all hidden nodes and edges.
     */
    showAllElements() {
        this.showAllNodes();
        this.showAllEdges();
    }
    /**
     * Get visible edges (where edge is not hidden and both source and target are visible).
     */
    getVisibleEdges() {
        return this.edges.filter(e => !e.hidden && !e.source.hidden && !e.target.hidden);
    }
    /**
     * Get all visible edges including inferred ones.
     */
    getAllVisibleEdges() {
        const visibleRegular = this.edges.filter(e => !e.hidden && !e.source.hidden && !e.target.hidden);
        const visibleInferred = this.inferredEdges.filter(e => !e.hidden && !e.source.hidden && !e.target.hidden);
        return [...visibleRegular, ...visibleInferred];
    }
    /**
     * Export visible diagram elements to Turtle format.
     * Includes only non-hidden individuals and ALL their assertions from the KnowledgeGraph.
     * The diagram filters individuals, not relationships - if an individual is visible,
     * all its object properties and data properties are exported.
     *
     * @param knowledgeGraph - The knowledge graph containing individuals and relationships
     * @param workspacePrefixes - Optional prefix map from Workspace.getPrefixes() (namespace → prefix)
     */
    toTurtle(knowledgeGraph, workspacePrefixes) {
        // Start with standard W3C prefixes (always needed)
        const knownPrefixes = {
            'http://www.w3.org/1999/02/22-rdf-syntax-ns#': 'rdf',
            'http://www.w3.org/2000/01/rdf-schema#': 'rdfs',
            'http://www.w3.org/2002/07/owl#': 'owl',
            'http://www.w3.org/2001/XMLSchema#': 'xsd',
        };
        // Add prefixes from workspace (all loaded ontologies)
        if (workspacePrefixes) {
            for (const [namespace, prefix] of workspacePrefixes) {
                knownPrefixes[namespace] = prefix;
            }
        }
        // Add the KG's own URI as the default namespace prefix
        // Ensure it ends with # or / for proper prefix usage
        const kgNamespace = knowledgeGraph.uri.endsWith('#') || knowledgeGraph.uri.endsWith('/')
            ? knowledgeGraph.uri
            : knowledgeGraph.uri + '#';
        knownPrefixes[kgNamespace] = '';
        // Collect which prefixes are actually used
        const usedPrefixes = new Set(['rdf', 'rdfs', 'owl']);
        // Helper to shorten a URI using known prefixes
        const formatUri = (uri) => {
            for (const [ns, prefix] of Object.entries(knownPrefixes)) {
                if (uri.startsWith(ns)) {
                    usedPrefixes.add(prefix);
                    return `${prefix}:${uri.substring(ns.length)}`;
                }
            }
            return `<${uri}>`;
        };
        // Helper to escape string literals
        const escapeString = (s) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        // Get visible elements
        const visibleNodes = this.getVisibleNodes();
        // Get ALL relationships from KnowledgeGraph where subject is visible on diagram
        const visibleUris = new Set(visibleNodes.map(dn => dn.modelElement.uri));
        const visibleRelationships = Array.from(knowledgeGraph.relationships.values()).filter(rel => visibleUris.has(rel.subject.uri));
        // Build a map of relationships by subject URI for grouping
        const relsBySubject = new Map();
        for (const rel of visibleRelationships) {
            const subjectUri = rel.subject.uri;
            if (!relsBySubject.has(subjectUri)) {
                relsBySubject.set(subjectUri, []);
            }
            relsBySubject.get(subjectUri).push(rel);
        }
        // Content lines (will prepend prefixes later)
        const contentLines = [];
        // Header comment
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push(`# Visible Diagram Elements: ${this.name}`);
        contentLines.push(`# Exported: ${new Date().toISOString()}`);
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('');
        // Ontology declaration with imports (from KnowledgeGraph)
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('# Ontology Declaration');
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('');
        // Build ontology declaration using KG's URI and imports
        const kgOntologyUri = knowledgeGraph.uri.endsWith('#') || knowledgeGraph.uri.endsWith('/')
            ? knowledgeGraph.uri.replace(/[#/]$/, '')
            : knowledgeGraph.uri;
        if (knowledgeGraph.imports.size === 0) {
            contentLines.push(`<${kgOntologyUri}> a owl:Ontology .`);
        }
        else {
            contentLines.push(`<${kgOntologyUri}> a owl:Ontology ;`);
            const importEntries = Array.from(knowledgeGraph.imports.entries());
            importEntries.forEach(([ontologyIri, versionIri], index) => {
                // Use versionIRI if available, otherwise ontologyIRI
                const importIri = versionIri || ontologyIri;
                const isLast = index === importEntries.length - 1;
                contentLines.push(`    owl:imports <${importIri}>${isLast ? ' .' : ' ;'}`);
            });
        }
        contentLines.push('');
        // Export visible individuals/classes
        if (visibleNodes.length > 0) {
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('# Individuals');
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('');
            for (const diagramNode of visibleNodes) {
                const modelElement = diagramNode.modelElement;
                const props = [];
                // Types (owl:NamedIndividual and the class if available)
                if (modelElement instanceof Individual) {
                    // ABox Individual - has modelClass reference
                    props.push(`    a owl:NamedIndividual ,`);
                    props.push(`      ${formatUri(modelElement.modelClass.uri)} ;`);
                }
                else if (isIndividual(modelElement)) {
                    // SchemaIndividual (TBox individual) - has classUris array
                    const classUri = modelElement.classUris[0];
                    if (classUri) {
                        props.push(`    a owl:NamedIndividual ,`);
                        props.push(`      ${formatUri(classUri)} ;`);
                    }
                    else {
                        props.push(`    a owl:NamedIndividual ;`);
                    }
                }
                else {
                    props.push(`    a owl:Class ;`);
                }
                // Label
                props.push(`    rdfs:label "${escapeString(modelElement.name)}"`);
                // Definition if available
                if (modelElement.definition) {
                    props[props.length - 1] += ' ;';
                    props.push(`    rdfs:comment "${escapeString(modelElement.definition)}"`);
                }
                // Data property values for individuals (supports multi-value)
                if (modelElement instanceof Individual) {
                    modelElement.propertyValues.forEach((values, propertyUri) => {
                        // Find the property definition to get the range/datatype
                        const dataProp = modelElement.modelClass.dataProperties?.find(p => p.uri === propertyUri);
                        const range = dataProp?.range?.toLowerCase() || 'string';
                        // Format each value based on datatype
                        for (const value of values) {
                            props[props.length - 1] += ' ;';
                            let formattedValue;
                            if (range === 'date' || range === 'datetime') {
                                formattedValue = `"${escapeString(value)}"^^xsd:date`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            }
                            else if (range === 'integer' || range === 'int' || range === 'long' || range === 'short') {
                                formattedValue = `"${escapeString(value)}"^^xsd:integer`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            }
                            else if (range === 'decimal' || range === 'float' || range === 'double') {
                                formattedValue = `"${escapeString(value)}"^^xsd:decimal`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            }
                            else if (range === 'boolean') {
                                formattedValue = `"${value === 'true' ? 'true' : 'false'}"^^xsd:boolean`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            }
                            else {
                                // Default to string
                                formattedValue = `"${escapeString(value)}"`;
                            }
                            props.push(`    ${formatUri(propertyUri)} ${formattedValue}`);
                        }
                    });
                }
                // Relationships where this element is the subject (from DomainModel)
                const rels = relsBySubject.get(modelElement.uri) || [];
                for (const rel of rels) {
                    props[props.length - 1] += ' ;';
                    const predicateUri = rel.predicate.uri;
                    const objectUri = rel.object.uri;
                    props.push(`    ${formatUri(predicateUri)} ${formatUri(objectUri)}`);
                }
                contentLines.push(formatUri(modelElement.uri));
                props[props.length - 1] += ' .';
                contentLines.push(...props);
                contentLines.push('');
            }
        }
        // Build prefix declarations (only for used prefixes)
        const prefixLines = [];
        const nsToPrefix = Object.entries(knownPrefixes);
        // Sort by prefix name for consistent output
        nsToPrefix.sort((a, b) => a[1].localeCompare(b[1]));
        for (const [ns, prefix] of nsToPrefix) {
            if (usedPrefixes.has(prefix)) {
                // Pad prefix for alignment
                const paddedPrefix = (prefix + ':').padEnd(10);
                prefixLines.push(`@prefix ${paddedPrefix}<${ns}> .`);
            }
        }
        prefixLines.push('');
        return [...prefixLines, ...contentLines].join('\n');
    }
    /**
     * Create a Diagram from JSON.
     * @param json - The serialized diagram data
     * @param knowledgeGraph - The KnowledgeGraph to look up ABox individuals (legacy, no longer used server-side)
     * @param _boundedContext - @deprecated Ontology content is now server-side only
     */
    static fromJSON(json, knowledgeGraph, _boundedContext) {
        // boundedContextId is required - use from JSON or fall back to a placeholder that must be set later
        const boundedContextId = json.boundedContextId ?? '__unassigned__';
        const diagram = new Diagram(json.name, boundedContextId, json.id);
        // Restore datasetId if present
        if (json.datasetId) {
            diagram.datasetId = json.datasetId;
        }
        // Restore edge style if present (support legacy 'relationshipStyle' key)
        const edgeStyleValue = json.edgeStyle ?? json.relationshipStyle;
        if (edgeStyleValue === 's-curve' || edgeStyleValue === 'arc') {
            diagram.edgeStyle = edgeStyleValue;
        }
        // Restore anchor mode if present
        if (json.anchorMode === 'auto' || json.anchorMode === 'dynamic' || json.anchorMode === 'centered') {
            diagram.anchorMode = json.anchorMode;
        }
        // Restore force layout enabled if present
        if (typeof json.forceLayoutEnabled === 'boolean') {
            diagram.forceLayoutEnabled = json.forceLayoutEnabled;
        }
        // Restore viewport transform if present
        if (json.viewportTransform && typeof json.viewportTransform.x === 'number' && typeof json.viewportTransform.y === 'number' && typeof json.viewportTransform.k === 'number') {
            diagram.viewportTransform = json.viewportTransform;
        }
        // Restore display options if present
        if (typeof json.showStereotype === 'boolean') {
            diagram.showStereotype = json.showStereotype;
        }
        if (typeof json.showEssentialProperties === 'boolean') {
            diagram.showEssentialProperties = json.showEssentialProperties;
        }
        if (typeof json.showOptionalProperties === 'boolean') {
            diagram.showOptionalProperties = json.showOptionalProperties;
        }
        if (typeof json.inferenceEnabled === 'boolean') {
            diagram.inferenceEnabled = json.inferenceEnabled;
        }
        if (json.editingMode === 'TBox' || json.editingMode === 'ABox') {
            diagram.editingMode = json.editingMode;
        }
        if (typeof json.modelingLanguageIri === 'string' || json.modelingLanguageIri === null) {
            diagram.modelingLanguageIri = json.modelingLanguageIri;
        }
        if (typeof json.labelingContextIri === 'string' || json.labelingContextIri === null) {
            diagram.labelingContextIri = json.labelingContextIri;
        }
        if (typeof json.showGrid === 'boolean') {
            diagram.showGrid = json.showGrid;
        }
        if (typeof json.showToolbar === 'boolean') {
            diagram.showToolbar = json.showToolbar;
        }
        if (typeof json.snapToGrid === 'boolean') {
            diagram.snapToGrid = json.snapToGrid;
        }
        if (typeof json.showDomainVisualization === 'boolean') {
            diagram.showDomainVisualization = json.showDomainVisualization;
        }
        // Build a map of element URI -> DiagramNode for edge linking
        // Key by modelElement.uri (concept.uri for SchemaClass, individualUri for Individual)
        const diagramNodeMap = new Map();
        // First pass: restore nodes (support legacy 'classes' key)
        const nodesJson = json.nodes ?? json.classes ?? [];
        for (const nodeJson of nodesJson) {
            const diagramNode = DiagramNode.fromJSON(nodeJson, knowledgeGraph);
            if (diagramNode) {
                diagram.addNode(diagramNode);
                // Key by the modelElement's uri (concept.uri for SchemaClass, individualUri for Individual)
                diagramNodeMap.set(diagramNode.modelElement.uri, diagramNode);
            }
        }
        // Second pass: restore edges (support legacy 'relationships' key)
        const edgesJson = json.edges ?? json.relationships ?? [];
        for (const edgeJson of edgesJson) {
            const diagramEdge = DiagramEdge.fromJSON(edgeJson, diagramNodeMap, knowledgeGraph);
            if (diagramEdge) {
                diagram.addEdge(diagramEdge);
            }
        }
        return diagram;
    }
}
